import { Drawer, Layout, Menu } from 'antd';

import { useSidebarContext } from '@/contexts/sidebar-context';

import { ContextSwitcher } from './context-switcher';
import { SidebarBottomSection } from './sidebar-bottom-section';
import {
  DRAWER_CONFIG,
  FIXED_SIDER_STYLE,
  type MenuItem,
} from './sidebar-utils';

const { Sider } = Layout;

interface BaseSidebarProps {
  menuItems: MenuItem[];
  getSelectedKeys: () => string[];
  renderTopSection?: (props: { isCollapsed: boolean }) => React.ReactNode;
}

/**
 * Base Sidebar Component
 * Provides a consistent sidebar structure with:
 * - Fixed positioning on desktop
 * - Drawer on mobile
 * - Scrollable menu area
 * - Sticky bottom section (EU logos + language switcher)
 */
export function BaseSidebar({
  menuItems,
  getSelectedKeys,
  renderTopSection,
}: BaseSidebarProps) {
  const { desktop, mobile } = useSidebarContext();

  return (
    <>
      {/* Desktop Sidebar */}
      <Sider
        collapsible
        collapsed={desktop.isCollapsed}
        onCollapse={desktop.setCollapsed}
        trigger={null}
        width={256}
        collapsedWidth={64}
        className="hidden lg:block"
        style={FIXED_SIDER_STYLE}
      >
        <div className="flex h-full flex-col py-4">
          {/* Top section (context switcher or custom) */}
          {renderTopSection ? (
            renderTopSection({ isCollapsed: desktop.isCollapsed })
          ) : (
            <ContextSwitcher isCollapsed={desktop.isCollapsed} />
          )}

          {/* Scrollable menu area */}
          <div className="flex-1 overflow-auto">
            <Menu
              mode="inline"
              selectedKeys={getSelectedKeys()}
              items={menuItems}
              className="border-0"
            />
          </div>

          {/* Sticky bottom section */}
          <SidebarBottomSection isCollapsed={desktop.isCollapsed} />
        </div>
      </Sider>

      {/* Mobile Drawer */}
      <Drawer
        placement={DRAWER_CONFIG.placement}
        onClose={mobile.close}
        open={mobile.isOpen}
        width={DRAWER_CONFIG.width}
        styles={DRAWER_CONFIG.styles}
        className={DRAWER_CONFIG.className}
      >
        <div className="flex h-full flex-col py-4">
          {/* Top section (context switcher or custom) */}
          {renderTopSection ? (
            renderTopSection({ isCollapsed: false })
          ) : (
            <ContextSwitcher isCollapsed={false} />
          )}

          {/* Scrollable menu area */}
          <div className="flex-1 overflow-auto">
            <Menu
              mode="inline"
              selectedKeys={getSelectedKeys()}
              items={menuItems}
              className="border-0"
            />
          </div>

          {/* Sticky bottom section */}
          <SidebarBottomSection isCollapsed={false} />
        </div>
      </Drawer>
    </>
  );
}
